import { createContext, useContext } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { useFlap, type FlapCell } from './useFlap';

interface Props {
  value: string;
  /** Pad on the left with blank cells to at least this many. */
  width?: number;
  delay?: number;
  /** `lg` is the headline figure of a slide, the default a row on the board. */
  size?: 'sm' | 'md' | 'lg';
  tone?: 'amber' | 'white' | 'green';
  className?: string;
}

/** True once the rider has tapped to finish the stop: the boards land at once. */
export const Hurry = createContext(false);

/**
 * A departure-board readout: each character is its own flap unit, and they
 * rattle through the drum to land on the value one after another.
 *
 * The visible cells are `aria-hidden`; the value is on the wrapper as a label,
 * so a screen reader hears "412" and not forty flipping letters.
 */
export function SplitFlap({
  value,
  width = 0,
  delay = 0,
  size = 'md',
  tone = 'amber',
  className = '',
}: Props) {
  const reduced = usePrefersReducedMotion();
  const text = value.toUpperCase().padStart(width, ' ');
  const hurry = useContext(Hurry);
  const cells = useFlap(text, { delay, instant: reduced || hurry });

  return (
    <span
      className={`sf sf-${size} sf-${tone} ${className}`.trim()}
      role="img"
      aria-label={value}
    >
      {cells.map((cell, i) => (
        <Unit key={i} cell={cell} />
      ))}
    </span>
  );
}

function Unit({ cell }: { cell: FlapCell }) {
  const { ch, prev, tick } = cell;
  const blank = ch === ' ';
  return (
    <span className={`sf-cell${blank ? ' blank' : ''}`} aria-hidden="true">
      {/* the half that is already showing the new character... */}
      <span className="sf-half sf-top">
        <i>{ch}</i>
      </span>
      {/* ...and the half still showing the old one, until the flap falls past it */}
      <span className="sf-half sf-bot">
        <i>{tick === 0 ? ch : prev}</i>
      </span>
      {tick > 0 && (
        <>
          <span className="sf-half sf-top sf-fold" key={`t${tick}`}>
            <i>{prev}</i>
          </span>
          <span className="sf-half sf-bot sf-unfold" key={`b${tick}`}>
            <i>{ch}</i>
          </span>
        </>
      )}
    </span>
  );
}
